import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ResultComponentProps } from '../common/types';
import BaseResultDisplay from '../common/BaseResultDisplay';
import { GridItem, regionColors } from './common/GridItem';
import RegionLegend from './common/RegionLegend';
import SequenceView from './common/SequenceView';
import { 
  Typography, 
  Modal, 
  Button, 
  Tag, 
  Tooltip, 
  Divider, 
  Table, 
  message, 
  Space, 
  Switch 
} from 'antd';
import { EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import { sequenceService } from '@/api/sequenceService';

// 禁用特定行的类型检查
// @ts-ignore
import type { ColumnsType } from 'antd/lib/table';

const { Title, Text } = Typography;

// 使用公共的区域颜色定义

interface BatchAntibodyResultProps extends ResultComponentProps {
  onExportAllCSV?: () => void;
  originalInput?: string; // 原始FASTA输入
}

// 序列详情模态框组件
const SequenceDetailModal = React.memo(({
  visible, 
  onClose, 
  data
}: { 
  visible: boolean, 
  onClose: () => void, 
  data: Record<string, any>
}) => {
  // 网格视图分页
  const [currentGridPage, setCurrentGridPage] = React.useState<number>(0);
  const itemsPerPage = 200; // 每页显示的氨基酸数量
  
  if (!data || !data.numbered_positions) {
    return (
      <Modal
        title="序列详情"
        open={visible}
        onCancel={onClose}
        footer={[
          <Button key="close" onClick={onClose}>关闭</Button>
        ]}
      >
        <Text type="warning">无法显示序列详情，数据格式不正确或为空。</Text>
      </Modal>
    );
  }

  const positions = data.numbered_positions;
  const regions = data.regions || {};
  
  // 使用公共图例组件
  
  // 使用共享序列视图组件

  // 渲染区域序列
  const renderRegionSequence = () => {
    return (
      <div>
        <Title level={5}>区域序列</Title>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {Object.entries(regions).map(([region, sequence]) => (
            <div key={region}>
              <Tag color={regionColors[region] || 'default'} style={{ marginRight: 8 }}>
                {region}
              </Tag>
              <Text style={{ fontFamily: 'monospace' }}>{String(sequence)}</Text>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  // 渲染编号网格视图
  const renderGridView = () => {
    const totalPositions = positions.length;
    const totalPages = Math.ceil(totalPositions / itemsPerPage);
    
    // 显示当前页的位置
    const startIndex = currentGridPage * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalPositions);
    const currentPositions = positions.slice(startIndex, endIndex);
    
    return (
      <div>
        <Title level={5}>编号网格视图</Title>
        <div style={{ marginBottom: '12px' }}>
          <Text>显示 {startIndex + 1}-{endIndex} 共 {totalPositions} 位点</Text>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '12px', backgroundColor: '#f9f9f9', borderRadius: '4px', border: '1px solid #eee' }}>
          {currentPositions.map((pos: any, index: number) => (
            <GridItem key={startIndex + index} pos={pos} index={startIndex + index} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <Modal
      title={`抗体序列详情 - ${data.title || '未命名'}`}
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="close" onClick={onClose}>
          关闭
        </Button>
      ]}
    >
      <RegionLegend />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {renderRegionSequence()}
        <Divider style={{ margin: '12px 0' }} />
        <SequenceView 
          positions={positions}
          regions={regions}
          lightMode={false}
          title="序列视图"
        />
        <Divider style={{ margin: '12px 0' }} />
        {renderGridView()}
      </div>
    </Modal>
  );
});

// 创建一个序列展示组件
const SequenceDisplay = ({ sequence, regions }: { sequence: string, regions: Record<string, string> }) => {
  // 如果有CDR区域信息，显示带颜色的序列
  if (regions.cdr1 || regions.cdr2 || regions.cdr3) {
    let elements: JSX.Element[] = [];
    
    // 简单实现：只高亮显示CDR区域
    // 尝试找到CDR区域在序列中的位置
    const cdrHighlights = [];
    
    if (regions.cdr1) {
      const start = sequence.indexOf(regions.cdr1);
      if (start >= 0) {
        cdrHighlights.push({
          start,
          end: start + regions.cdr1.length,
          color: regionColors.CDR1,
          name: 'CDR1'
        });
      }
    }
    
    if (regions.cdr2) {
      const start = sequence.indexOf(regions.cdr2);
      if (start >= 0) {
        cdrHighlights.push({
          start,
          end: start + regions.cdr2.length,
          color: regionColors.CDR2,
          name: 'CDR2'
        });
      }
    }
    
    if (regions.cdr3) {
      const start = sequence.indexOf(regions.cdr3);
      if (start >= 0) {
        cdrHighlights.push({
          start,
          end: start + regions.cdr3.length,
          color: regionColors.CDR3,
          name: 'CDR3'
        });
      }
    }
    
    // 如果找到了CDR区域，生成带高亮的序列
    if (cdrHighlights.length > 0) {
      // 按照起始位置排序
      cdrHighlights.sort((a, b) => a.start - b.start);
      
      let lastEnd = 0;
      cdrHighlights.forEach((highlight, index) => {
        // 添加CDR区域前的序列
        if (highlight.start > lastEnd) {
          elements.push(
            <span key={`pre-${index}`}>
              {sequence.substring(lastEnd, highlight.start)}
            </span>
          );
        }
        
        // 添加CDR区域
        elements.push(
          <Tooltip key={`cdr-${index}`} title={highlight.name}>
            <span style={{ color: highlight.color, fontWeight: 'bold' }}>
              {sequence.substring(highlight.start, highlight.end)}
            </span>
          </Tooltip>
        );
        
        lastEnd = highlight.end;
      });
      
      // 添加最后一个CDR区域后的序列
      if (lastEnd < sequence.length) {
        elements.push(
          <span key="post-last">
            {sequence.substring(lastEnd)}
          </span>
        );
      }
      
      return <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'monospace' }}>{elements}</div>;
    }
  }
  
  // 如果没有CDR区域信息，直接显示序列
  return <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'monospace' }}>{sequence}</div>;
};

// FASTA解析函数
const parseFastaSequences = (fastaText: string): Array<{id: string, sequence: string}> => {
  if (!fastaText || typeof fastaText !== 'string') {
    return [];
  }
  
  const sequences: Array<{id: string, sequence: string}> = [];
  const lines = fastaText.trim().split('\n');
  let currentId = '';
  let currentSequence = '';
  
  for (const line of lines) {
    const trimmedLine = line.trim();
    if (trimmedLine.startsWith('>')) {
      // 如果已经有了序列，先保存上一个
      if (currentId && currentSequence) {
        sequences.push({
          id: currentId,
          sequence: currentSequence
        });
      }
      // 开始新的序列
      currentId = trimmedLine.substring(1).trim(); // 去掉 '>'
      currentSequence = '';
    } else if (trimmedLine && !trimmedLine.startsWith(';')) {
      // 继续当前序列（忽略注释行）
      currentSequence += trimmedLine;
    }
  }
  
  // 保存最后一个序列
  if (currentId && currentSequence) {
    sequences.push({
      id: currentId,
      sequence: currentSequence
    });
  }
  
  return sequences;
};

// 批量抗体编号结果组件
const BatchAntibodyResult: React.FC<BatchAntibodyResultProps> = ({ data, title, onDeleteResult, multiResults, onExportAllCSV, originalInput }) => {
  const [tableData, setTableData] = useState<any[]>([]);
  const [detailModalVisible, setDetailModalVisible] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [alignMode, setAlignMode] = useState<boolean>(false);
  const [alignLoading, setAlignLoading] = useState<boolean>(false);
  const [alignedData, setAlignedData] = useState<{ names: string[]; aligned_sequences: string[] } | null>(null);

  // 处理数据，完全依赖前端解析FASTA输入来获取序列ID
  useEffect(() => {
    try {
      console.log('BatchAntibodyResult - 原始数据:', data);
      console.log('BatchAntibodyResult - 原始输入:', originalInput);
      
      // 解析原始FASTA输入获取序列ID
      let fastaSequences: Array<{id: string, sequence: string}> = [];
      if (originalInput) {
        fastaSequences = parseFastaSequences(originalInput);
        console.log('BatchAntibodyResult - 解析的FASTA序列:', fastaSequences);
      }
      
      // 获取序列ID的函数，优先使用后端返回的name字段
      const getSequenceId = (item: any, index: number): string => {
        // 优先使用后端返回的name字段
        if (item.name) {
          return item.name;
        }
        // 如果没有name，尝试使用FASTA解析结果
        if (fastaSequences.length > index && fastaSequences[index].id) {
          return fastaSequences[index].id;
        }
        // 最后使用默认命名
        return `序列${index + 1}`;
      };
      
      if (Array.isArray(data)) {
        // 多条序列数据
        const formattedData = data.map((item, index) => {
          const resultData = item?.result || item?.data || item;
          return {
            key: String(index + 1),
            id: getSequenceId(resultData, index),
            sequence: resultData?.sequence,
            rawData: resultData,
          };
        });
        setTableData(formattedData);
      } else if (Array.isArray(data.results)) {
        // 多条序列数据存储在results数组中
        const formattedData = data.results.map((item: any, index: number) => {
          const resultData = item?.result || item?.data || item;
          return {
            key: String(index + 1),
            id: getSequenceId(resultData, index),
            sequence: resultData?.sequence,
            rawData: resultData,
            originalIndex: resultData?.originalIndex !== undefined ? resultData.originalIndex : index
          };
        });
        // 按originalIndex排序，确保显示顺序与输入顺序一致
        formattedData.sort((a: {originalIndex: number}, b: {originalIndex: number}) => a.originalIndex - b.originalIndex);
        setTableData(formattedData);
      } else if (data && data.results && typeof data.results === 'object') {
        // 后端批量FASTA新返回：以字典形式返回 { [name]: result }
        // 需要按FASTA原始顺序排序
        const nameOrder: string[] = (fastaSequences && fastaSequences.length)
          ? fastaSequences.map(f => f.id)
          : Object.keys(data.results);

        const items: any[] = nameOrder
          .filter((name) => data.results[name])
          .map((name, index) => {
            const resultData = data.results[name];
            return {
              key: String(index + 1),
              id: resultData?.name || name || `序列${index + 1}`,
              sequence: resultData?.sequence,
              rawData: resultData,
              originalIndex: index,
            };
          });

        setTableData(items);
      } else {
        // 单条序列数据
        const resultData = data?.result || data;
        const sequenceId = resultData?.name || 
          (fastaSequences.length > 0 && fastaSequences[0].id ? fastaSequences[0].id : null) ||
          title?.replace(/抗体编号结果 - /g, '') || '序列 1';
        
        const formattedData = {
          key: '1',
          id: sequenceId,
          sequence: resultData?.sequence,
          rawData: resultData,
        };
        setTableData([formattedData]);
      }
      
      console.log('BatchAntibodyResult - 最终表格数据:', tableData);
    } catch (error) {
      console.error('BatchAntibodyResult - 处理数据时出错:', error);
      message.error('处理数据时出错');
    }
  }, [data, title, originalInput]);

  const alignmentCacheRef = useRef<Map<string, { names: string[]; aligned_sequences: string[] }>>(new Map());

  useEffect(() => {
    alignmentCacheRef.current.clear();
  }, [data]);

  // 计算多序列比对
  const computeAlignment = useCallback(async () => {
    if (!tableData.length) {
      setAlignedData(null);
      return;
    }
    try {
      setAlignLoading(true);
      const sequences: string[] = tableData.map((r) => String(r.rawData?.sequence || r.sequence || ''));
      const names: string[] = tableData.map((r) => String(r.id || 'seq'));
      const cacheKey = JSON.stringify({ sequences, names, sequence_type: 'protein' });
      const cachedResult = alignmentCacheRef.current.get(cacheKey);
      if (cachedResult) {
        setAlignedData(cachedResult);
        return;
      }

      const resp: any = await sequenceService.alignSequences({ sequences, names, sequence_type: 'protein' });
      const result = resp?.data || resp; // 兼容返回结构
      if (result && Array.isArray(result.aligned_sequences)) {
        const formatted = { names: result.names || names, aligned_sequences: result.aligned_sequences };
        alignmentCacheRef.current.set(cacheKey, formatted);
        setAlignedData(formatted);
      } else {
        setAlignedData(null);
        message.warning('比对结果为空');
      }
    } catch (err: any) {
      console.error('Alignment error:', err);
      message.error('序列对齐失败');
      setAlignedData(null);
    } finally {
      setAlignLoading(false);
    }
  }, [tableData]);

  // 当开启对齐或数据变化时触发比对
  useEffect(() => {
    if (alignMode) {
      computeAlignment();
    }
  }, [alignMode, tableData, computeAlignment]);

  // 渲染对齐视图并保持CDR着色
  const renderAlignedView = useCallback(() => {
    if (!alignedData || !alignedData.aligned_sequences?.length) return null;

    const alignedSequences = alignedData.aligned_sequences;
    
    // 为每条序列构建未对齐索引到区域的映射
    const regionMaps = tableData.map((r) => {
      const positions: any[] = r.rawData?.numbered_positions || [];
      // positions 按顺序对应未对齐氨基酸
      return positions.map((p) => p.region || '');
    });

    // 颜色获取辅助
    const getColor = (region: string) => regionColors[region] || '#000';

    // 氨基酸颜色映射（用于不同氨基酸类型）
    const aminoAcidColors: { [key: string]: string } = {
      'A': '#FF6B6B', 'R': '#4ECDC4', 'N': '#45B7D1', 'D': '#96CEB4', 'C': '#FFEAA7',
      'Q': '#DDA0DD', 'E': '#98D8C8', 'G': '#F7DC6F', 'H': '#BB8FCE', 'I': '#85C1E9',
      'L': '#F8C471', 'K': '#82E0AA', 'M': '#F1948A', 'F': '#85C1E9', 'P': '#D7BDE2',
      'S': '#A9DFBF', 'T': '#F9E79F', 'W': '#AED6F1', 'Y': '#F5B7B1', 'V': '#D5A6BD',
      '-': '#E74C3C' // gap用红色
    };

    // 分析每个位置的氨基酸分布和最频繁氨基酸
    const maxLength = Math.max(...alignedSequences.map(seq => seq.length));
    const positionAnalysis: { [pos: number]: { 
      counts: { [aa: string]: number }, 
      mostFrequent: string,
      isIdentical: boolean,
      colorMap: { [aa: string]: { color: string, isDefault: boolean } }
    } } = {};
    
    for (let pos = 0; pos < maxLength; pos++) {
      const counts: { [aa: string]: number } = {};
      const aasAtPos: string[] = [];
      
      // 统计该位置的氨基酸分布
      alignedSequences.forEach(seq => {
        if (pos < seq.length) {
          const aa = seq[pos];
          aasAtPos.push(aa);
          counts[aa] = (counts[aa] || 0) + 1;
        }
      });
      
      // 找出最频繁的氨基酸
      let mostFrequent = '';
      let maxCount = 0;
      for (const [aa, count] of Object.entries(counts)) {
        if (count > maxCount) {
          maxCount = count;
          mostFrequent = aa;
        }
      }
      
      // 判断是否所有氨基酸都相同
      const uniqueAAs = Object.keys(counts);
      const isIdentical = uniqueAAs.length === 1;
      
      // 构建颜色映射
      const colorMap: { [aa: string]: { color: string, isDefault: boolean } } = {};
      
      if (isIdentical) {
        // 所有氨基酸相同 → 黑色
        colorMap[mostFrequent] = { color: '#000', isDefault: true };
      } else {
        // 有变异的情况
        for (const aa of uniqueAAs) {
          if (aa === mostFrequent) {
            // 最频繁的氨基酸 → 黑色
            colorMap[aa] = { color: '#000', isDefault: true };
          } else {
            // 其他氨基酸 → 对应颜色
            colorMap[aa] = { color: aminoAcidColors[aa] || '#666', isDefault: false };
          }
        }
      }
      
      positionAnalysis[pos] = {
        counts,
        mostFrequent,
        isIdentical,
        colorMap
      };
    }

    // 参考序列(第1条)作为区域边界的基准
    const ref = alignedSequences[0] || '';
    const maxLen = ref.length;
    const refRegionByCol: string[] = new Array(maxLen).fill('');
    let refUnalignedIdx = 0;
    for (let j = 0; j < maxLen; j++) {
      if (ref[j] !== '-') {
        refRegionByCol[j] = regionMaps[0]?.[refUnalignedIdx] || '';
        refUnalignedIdx += 1;
      } else {
        // 对于gap位置，使用前一个位置的区域信息
        refRegionByCol[j] = j > 0 ? refRegionByCol[j - 1] : '';
      }
    }
    
    // 找出区域边界位置（FR1→CDR1, CDR1→FR2, FR2→CDR2, CDR2→FR3, CDR3→FR4）
    const regionBoundaries: Array<{
      position: number;
      fromRegion: string;
      toRegion: string;
      beforePos: number;
      afterPos: number;
    }> = [];
    
    for (let j = 1; j < maxLen; j++) {
      if (refRegionByCol[j] !== refRegionByCol[j - 1]) {
        const fromRegion = refRegionByCol[j - 1];
        const toRegion = refRegionByCol[j];
        
        // 只在有意义的FR/CDR边界处添加垂直线
        // 跳过空区域或相同类型的区域转换
        if (fromRegion && toRegion && fromRegion !== toRegion) {
          // 使用对齐序列的位置索引（1-based）
          const beforePos = j; // 前一个位置（1-based）
          const afterPos = j + 1; // 当前位置（1-based）
          
          regionBoundaries.push({
            position: j,
            fromRegion,
            toRegion,
            beforePos,
            afterPos
          });
        }
      }
    }


    const cellStyle: React.CSSProperties = {
      display: 'inline-block',
      width: '1.2em',
      textAlign: 'center',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      lineHeight: 1.6,
      fontSize: '14px',
      position: 'relative',
    };

    return (
      <div style={{ border: '1px solid #eee', borderRadius: 4, padding: 12, background: '#fafafa', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Typography.Title level={5} style={{ margin: 0 }}>对齐结果</Typography.Title>
        </div>
        <div style={{ position: 'relative', display: 'flex' }}>
          {/* 固定的ID列 */}
          <div style={{ width: 160, flex: '0 0 auto', zIndex: 2, backgroundColor: '#fafafa', borderRight: '1px solid #eee' }}>
            {alignedSequences.map((_, idx) => {
              const name = alignedData.names?.[idx] || `seq${idx+1}`;
              return (
                <div key={`id-${idx}`} style={{ height: '28px', lineHeight: '28px', fontFamily: cellStyle.fontFamily, fontSize: cellStyle.fontSize, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '0 8px' }}>
                  {name}
                </div>
              );
            })}
          </div>
          
          {/* 可滚动的序列内容 */}
          <div style={{ overflowX: 'auto', position: 'relative', flex: '1 1 auto' }}>
            <div style={{ fontFamily: cellStyle.fontFamily, whiteSpace: 'nowrap', fontSize: cellStyle.fontSize, position: 'relative' }}>
              {/* 区域边界垂直线 */}
              {regionBoundaries.map((boundary, idx) => (
                <div
                  key={`boundary-${idx}`}
                  style={{
                    position: 'absolute',
                    left: `${12 + boundary.position * 1.2 * 14}px`, // 12px gap + 位置计算 (移除了160px序列名的偏移)
                    top: '-30px',
                    height: 'calc(100% + 30px)',
                    width: '1px',
                    backgroundColor: '#999',
                    zIndex: 10,
                    pointerEvents: 'none'
                  }}
                >
                  {/* 左侧数字 */}
                  <div style={{
                    position: 'absolute',
                    top: '0px',
                    right: '3px',
                    fontSize: '9px',
                    color: '#666',
                    backgroundColor: 'white',
                    padding: '1px 2px',
                    borderRadius: '2px',
                    border: '1px solid #ccc'
                  }}>
                    {boundary.beforePos}
                  </div>
                  {/* 右侧数字 */}
                  <div style={{
                    position: 'absolute',
                    top: '0px',
                    left: '3px',
                    fontSize: '9px',
                    color: '#666',
                    backgroundColor: 'white',
                    padding: '1px 2px',
                    borderRadius: '2px',
                    border: '1px solid #ccc'
                  }}>
                    {boundary.afterPos}
                  </div>
                </div>
              ))}
              
              {/* 序列行 */}
              {alignedSequences.map((aseq, idx) => {
                let unalignedIndex = 0;
                const colored = Array.from(aseq).map((ch, j) => {
                  let region = '';
                  let isCDR = false;
                  let color = '#000';
                  
                  if (ch !== '-') {
                    region = regionMaps[idx]?.[unalignedIndex] || '';
                    isCDR = region.startsWith?.('CDR');
                    color = getColor(region);
                    unalignedIndex += 1;
                  }

                  // 使用新的智能颜色逻辑
                  const posAnalysis = positionAnalysis[j];
                  const colorInfo = posAnalysis?.colorMap[ch];
                  const finalColor = colorInfo?.color || '#666';
                  const isDefaultColor = colorInfo?.isDefault || false;
                  
                  const spanStyle: React.CSSProperties = {
                    ...cellStyle,
                    color: finalColor,
                    fontWeight: isCDR ? 700 : 400,
                    backgroundColor: isDefaultColor ? 'transparent' : 'rgba(255, 255, 255, 0.8)',
                    borderRadius: isDefaultColor ? '0' : '2px',
                    border: isDefaultColor ? 'none' : `1px solid ${finalColor}`,
                    padding: isDefaultColor ? '0' : '1px',
                  };

                  return (
                    <Tooltip key={j} title={ch === '-' ? 'Gap' : (region || 'FR')}>
                      <span style={spanStyle}>{ch}</span>
                    </Tooltip>
                  );
                });
                return (
                  <div key={idx} style={{ display: 'flex', lineHeight: '28px', height: '28px', fontFamily: 'monospace', position: 'relative', zIndex: 1 }}>
                    <div style={{ whiteSpace: 'pre', position: 'relative', paddingLeft: '12px' }}>{colored}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }, [alignedData, tableData]);

  // 定义表格列
  // 定义表格列
  const columns = useMemo(() => {
    // 基础列定义
    const baseColumns: ColumnsType<any> = [
      {
        title: '序列ID',
        dataIndex: 'id',
        key: 'id',
        width: 150,
        fixed: 'left' as 'left',
        ellipsis: true,
      },
      {
        title: '物种',
        key: 'species',
        width: 100,
        // @ts-ignore
        render: (_, record) => {
          const species = record.rawData?.species || '未知';
          return <Text style={{ fontSize: '12px' }}>{species}</Text>;
        }
      },
      {
        title: '链类型',
        key: 'chain_type',
        width: 80,
        // @ts-ignore
        render: (_, record) => {
          const chainType = record.rawData?.chain_type || '未知';
          const color = chainType === 'heavy' ? 'blue' : chainType === 'light' ? 'green' : 'default';
          return <Tag color={color} style={{ fontSize: '11px' }}>{chainType}</Tag>;
        }
      },
      {
        title: '序列',
        dataIndex: 'sequence',
        key: 'sequence',
        // @ts-ignore 忽略类型检查，解决渲染函数的类型问题
        render: (_, record) => {
          const sequence = String(record.sequence || '');
          const regions = record.rawData?.regions || {};
          
          return <SequenceDisplay sequence={sequence} regions={regions} />;
        }
      },
      {
        title: '操作',
        key: 'action',
        width: 80,
        fixed: 'right' as 'right',
        // @ts-ignore 忽略类型检查，解决渲染函数的类型问题
        render: (_, record) => (
          <Button 
            type="primary" 
            icon={<EyeOutlined />} 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              setSelectedItem(record.rawData);
              setDetailModalVisible(true);
            }}
          >
            详情
          </Button>
        ),
      }
    ];

    // 如果只有一条记录，则不显示ID列
    if (tableData.length <= 1) {
      return baseColumns.filter(col => col.key !== 'id');
    }
    
    return baseColumns;
  }, [tableData]);
  
  // 导出CSV文件
  const handleExportCSV = useCallback(async () => {
    try {
      if (!tableData.length) {
        message.info('没有可导出的数据');
        return;
      }
      
      // 准备CSV表头
      const headers = [
        '序列ID',
        '物种',
        '链类型',
        '序列',
        '序列长度',
        '编码方案',
        'CDR定义方案',
        'FR1',
        'CDR1',
        'FR2',
        'CDR2',
        'FR3',
        'CDR3',
        'FR4'
      ].join(',');
      
      // 准备CSV行数据
      const rows = tableData.map((item: any) => {
        const regions = item.rawData?.regions || {};
        
        // 格式化链类型
        const rawChainType = item.rawData?.chain_type || '未知';
        let chainType = '';
        if (rawChainType === 'H') chainType = '重链';
        else if (rawChainType === 'K') chainType = '轻链 (Kappa)';
        else if (rawChainType === 'L') chainType = '轻链 (Lambda)';
        else chainType = rawChainType;
        
        const row = [
          `"${item.id}"`,
          `"${item.rawData?.species || '未知'}"`,
          `"${chainType}"`,
          `"${item.rawData?.sequence || ''}"`,
          String(item.rawData?.sequence?.length || 0),
          `"${item.rawData?.scheme || ''}"`,
          `"${item.rawData?.cdr_definition || ''}"`,
          `"${regions.fr1 || ''}"`,
          `"${regions.cdr1 || ''}"`,
          `"${regions.fr2 || ''}"`,
          `"${regions.cdr2 || ''}"`,
          `"${regions.fr3 || ''}"`,
          `"${regions.cdr3 || ''}"`,
          `"${regions.fr4 || ''}"`
        ].join(',');
        
        return row;
      });
      
      // 创建CSV内容
      const csvContent = `${headers}\n${rows.join('\n')}`;
      
      // 创建并下载CSV文件
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `抗体编号结果_批量_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      message.success(`成功导出 ${rows.length} 条抗体序列数据`);
    } catch (error) {
      console.error('导出CSV文件失败:', error);
      message.error('导出CSV文件失败');
    }
  }, [tableData]);
  
  // 根据序列数量决定标题
  const displayTitle = useMemo(() => {
    if (tableData.length > 1) {
      return title || "批量抗体编号结果";
    }
    return title || "抗体编号结果";
  }, [title, tableData.length]);

  return (
    <BaseResultDisplay
      data={data}
      title={displayTitle}
      onDeleteResult={onDeleteResult}
      multiResults={multiResults}
      showRawData={false}
    >
      {/* 工具栏 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Space>
          <Switch checked={alignMode} onChange={setAlignMode} />
          <Text>对齐模式</Text>
        </Space>

      </div>

      {/* 对齐视图 */}
      {alignMode && renderAlignedView()}

      <Table 
        dataSource={tableData} 
        columns={columns}
        pagination={{ 
          pageSize: 1000, 
          showSizeChanger: true, 
          pageSizeOptions: ['100', '500', '1000', '2000'],
          showTotal: (total) => `共 ${total} 条记录`
        }}
        size="small"
        bordered
        scroll={{ x: 'max-content' }}
        onRow={(record) => ({
          onClick: () => {
            setSelectedItem(record.rawData);
            setDetailModalVisible(true);
          }
        })}
        rowClassName={() => 'clickable-row'}
      />
      
      {/* 序列详情模态框 */}
      {selectedItem && (
        <SequenceDetailModal 
          visible={detailModalVisible}
          onClose={() => setDetailModalVisible(false)}
          data={selectedItem}
        />
      )}

      <style dangerouslySetInnerHTML={{
        __html: `
        .clickable-row {
          cursor: pointer;
        }
        .clickable-row:hover {
          background-color: #f5f5f5;
        }
        `
      }} />
    </BaseResultDisplay>
  );
};

export default BatchAntibodyResult;